import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Form, Input, Select, Typography } from "antd";
import { ArrowLeftOutlined, CheckCircleFilled } from "@ant-design/icons";
import axiosClient from "../api/axiosClient";

const { Title, Text, Paragraph } = Typography;

type DemoRequestValues = {
  full_name: string;
  email: string;
  phone: string;
  business_name: string;
  staff_count: string;
  message?: string;
};

export default function DemoRequestPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm<DemoRequestValues>();
  const [submitting, setSubmitting] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  async function handleSubmit(values: DemoRequestValues) {
    setSubmitting(true);
    try {
      await axiosClient.post("/demo-requests", values);
      setSubmittedEmail(values.email);
    } catch {
      // fall back to still showing confirmation copy is misleading; surface the error instead
      form.setFields([{ name: "email", errors: ["Something went wrong — please try again."] }]);
    } finally {
      setSubmitting(false);
    }
  }

  if (submittedEmail) {
    return (
      <div style={{ minHeight: "100vh", background: "#f9fafb", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <Card style={{ maxWidth: 560, width: "100%", textAlign: "center", borderRadius: 16 }}>
          <CheckCircleFilled style={{ fontSize: 64, color: "#16a34a", marginBottom: 16 }} />
          <Title level={2}>Thank You!</Title>
          <Paragraph style={{ fontSize: 16 }}>
            We've received your demo request. Our team will reach out to you within 24 hours to schedule your
            personalized demo.
          </Paragraph>
          <Paragraph type="secondary">
            In the meantime, check your email at <Text strong>{submittedEmail}</Text> for more information about
            Hairplane.
          </Paragraph>
          <Button type="primary" size="large" onClick={() => navigate("/")}>
            Back to Home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f9fafb" }}>
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "24px 24px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate("/")} style={{ paddingLeft: 0, marginBottom: 8 }}>
            Back to Home
          </Button>
          <Title level={2} style={{ marginBottom: 4 }}>Request a Demo</Title>
          <Text type="secondary">
            See how Hairplane can transform your salon business. We'll give you a personalized walkthrough of all
            features.
          </Text>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "32px 24px", display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 32 }}>
        <Card style={{ borderRadius: 16 }}>
          <Title level={4}>Tell Us About Your Business</Title>
          <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 16 }}>
            <Form.Item name="full_name" label="Full Name" rules={[{ required: true, message: "Full name is required" }]}>
              <Input size="large" placeholder="John Smith" />
            </Form.Item>
            <Form.Item name="email" label="Email" rules={[{ required: true, type: "email", message: "A valid email is required" }]}>
              <Input size="large" placeholder="john@salon.com" />
            </Form.Item>
            <Form.Item name="phone" label="Phone" rules={[{ required: true, message: "Phone is required" }]}>
              <Input size="large" placeholder="(415) 555-0123" />
            </Form.Item>
            <Form.Item name="business_name" label="Business Name" rules={[{ required: true, message: "Business name is required" }]}>
              <Input size="large" placeholder="Your Salon Name" />
            </Form.Item>
            <Form.Item name="staff_count" label="Number of Staff Members" rules={[{ required: true, message: "Please select a staff count" }]}>
              <Select
                size="large"
                placeholder="Select..."
                options={[
                  { value: "1", label: "Just me" },
                  { value: "2-5", label: "2-5 staff" },
                  { value: "6-10", label: "6-10 staff" },
                  { value: "11-20", label: "11-20 staff" },
                  { value: "20+", label: "20+ staff" },
                ]}
              />
            </Form.Item>
            <Form.Item name="message" label="What are you looking for? (Optional)">
              <Input.TextArea rows={4} placeholder="Tell us about your needs, current challenges, or specific features you're interested in..." />
            </Form.Item>
            <Button type="primary" htmlType="submit" block size="large" loading={submitting} style={{ height: 52, fontWeight: 600 }}>
              Request Demo
            </Button>
            <Text type="secondary" style={{ display: "block", textAlign: "center", marginTop: 12 }}>
              By submitting, you agree to our Terms of Service and Privacy Policy
            </Text>
          </Form>
        </Card>

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <Card style={{ background: "linear-gradient(135deg,#2563eb,#7c3aed)", color: "#fff", borderRadius: 16, border: "none" }}>
            <Title level={4} style={{ color: "#fff" }}>What You'll Get</Title>
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              <li>30-minute personalized demo tailored to your business</li>
              <li>See how Hairplane can increase your revenue</li>
              <li>Get answers to all your questions</li>
              <li>Free 14-day trial — no credit card required</li>
            </ul>
          </Card>
          <Card style={{ borderRadius: 16 }}>
            <Text strong>Join 2,500+ Salons</Text>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">Trusted by industry leaders · Average rating: 4.9/5</Text>
            </div>
          </Card>
          <Card style={{ background: "#f0fdf4", borderColor: "#bbf7d0", borderRadius: 16 }}>
            <Text strong style={{ color: "#166534" }}>🎉 Limited Time Offer</Text>
            <Paragraph style={{ color: "#15803d", marginTop: 8, marginBottom: 0 }}>
              Get 20% off your first 3 months when you sign up during your demo call!
            </Paragraph>
          </Card>
        </div>
      </div>
    </div>
  );
}
